import React, { useEffect, useRef, useState } from 'react';
import { Article } from '../types';
import { 
  tailwindAnimationOptimizer, 
  animationUtils,
  TAILWIND_ANIMATIONS,
  PERFORMANCE_CLASSES 
} from '../utils/tailwindAnimationOptimizer';
import { debugAnimationPerformance } from '../utils/animationCacheService';
import ArticleThumbnail from '../components/ArticleThumbnail';
import '../styles/Articles.css';

// Hardcoded articles data to avoid Medium RSS fetching errors
const hardcodedArticles: Article[] = [
  {
    id: 'why-i-wrote-a-language',
    title: 'Why I Wrote A Language',
    description: 'Prism — a comprehensive abstract for the bridge between humans and AI. Exploring the philosophy behind creating a programming language that treats code elegance as mathematically measurable and bridges the gap between human intuition and AI comprehension.',
    thumbnail: '🔺',
    link: 'https://griffinn.medium.com/why-i-wrote-a-language-62180b64d1af',
    publishedDate: '2025-09-21',
    readingTime: '10 min',
    tags: ['Programming Languages', 'Prism', 'AI', 'Compilers', 'Language Design', 'Mathematics', 'Philosophy'],
    claps: 342,
    responses: 18
  },
  {
    id: 'beyond-a-line-count',
    title: 'Beyond A Line Count',
    description: 'Visualizing Progress with HowMany and Stackview. A comprehensive look at code analysis tools that go beyond simple line counting to provide meaningful insights into code quality and project structure.',
    thumbnail: '📊',
    link: 'https://griffinn.medium.com/beyond-a-line-count-cb8f645015b7',
    publishedDate: '2025-07-27',
    readingTime: '4 min',
    tags: ['Code Analysis', 'Rust', 'Development', 'Tools', 'Visualization', 'TUI', 'AST'],
    claps: 287,
    responses: 24
  },
  {
    id: 'rebuilding-earth',
    title: 'Rebuilding Earth',
    description: 'One small step for man, one massive migraine for me.',
    thumbnail: '🌍',
    link: 'https://griffinn.medium.com/rebuilding-earth-abc123',
    publishedDate: '2024-04-23',
    readingTime: '5 min',
    tags: ['Earth', 'Rebuilding', 'Environment', 'Technology'],
    claps: 156,
    responses: 12
  },
  {
    id: 'faxmachine-file-navigation',
    title: 'Elevating File Reuse with FaxMachine: My Journey to Intelligent File Navigation and One-Step…',
    description: 'The FaxMachine system and how it changed my workflow.',
    thumbnail: '📠',
    link: 'https://griffinn.medium.com/elevating-file-reuse-with-faxmachine-def456',
    publishedDate: '2024-04-07',
    readingTime: '8 min',
    tags: ['Productivity', 'File Management', 'Workflow', 'Development'],
    claps: 243,
    responses: 18
  },
  {
    id: 'safespace-testing-software',
    title: 'Why I Built SafeSpace — and Why It\'s Changing How I Test Software',
    description: 'The way a simple program changed my testing workflow forever.',
    thumbnail: '🛡️',
    link: 'https://griffinn.medium.com/why-i-built-safespace-ghi789',
    publishedDate: '2024-04-04',
    readingTime: '6 min',
    tags: ['Testing', 'Software', 'Development', 'Tools'],
    claps: 189,
    responses: 22
  },
  {
    id: 'qgit-versioning-simplified',
    title: 'QGit — Versioning Simplified',
    description: 'The ultimate version control tool, making everything easier.',
    thumbnail: '🔄',
    link: 'https://griffinn.medium.com/qgit-versioning-simplified-jkl012',
    publishedDate: '2024-04-02',
    readingTime: '7 min',
    tags: ['Git', 'Version Control', 'Development', 'Productivity'],
    claps: 321,
    responses: 15
  }
];

const Articles: React.FC = () => {
  const articlesRef = useRef<HTMLElement>(null);
  const articleCardsRef = useRef<HTMLDivElement[]>([]);
  const [filter, setFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [animationsReady, setAnimationsReady] = useState(false);

  // Load hardcoded articles on component mount
  useEffect(() => {
    const loadArticles = async () => {
      try {
        setLoading(true);
        
        // Sort articles by date (newest first)
        const sortedArticles = [...hardcodedArticles].sort((a, b) => 
          new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime()
        );
        
        setArticles(sortedArticles);
      } catch (err) {
        setError('Failed to load articles. Please try again later.');
        console.error('Error loading articles:', err);
      } finally {
        setLoading(false);
      }
    };

    loadArticles();
  }, []);

  // Setup optimized animations when articles are loaded
  useEffect(() => {
    if (!loading && articles.length > 0) {
      // Wait for DOM to be ready
      requestAnimationFrame(() => {
        setTimeout(() => {
          setupOptimizedAnimations();
          setAnimationsReady(true);
        }, 100);
      });
    }
  }, [loading, articles]);

  const setupOptimizedAnimations = () => {
    // Get all article card elements
    const articleCards = Array.from(document.querySelectorAll('.articles__card')) as HTMLElement[];
    articleCardsRef.current = articleCards as HTMLDivElement[];

    if (articleCards.length > 0) {
      // Batch optimize elements for performance
      tailwindAnimationOptimizer.batchOptimizeElements(articleCards);

      // Setup scroll-triggered animations with staggering
      tailwindAnimationOptimizer.setupScrollAnimations(
        articleCards,
        'fadeIn',
        {
          threshold: 0.1,
          rootMargin: '0px 0px -50px 0px',
          once: true,
          stagger: 100
        }
      );

      // Setup hover animations for article cards
      animationUtils.setupHoverAnimations(articleCards);

      // Add CSS classes for optimized animations
      articleCards.forEach((card, index) => {
        // Add base performance classes
        card.classList.add(
          'transform-gpu',
          'will-change-transform',
          'motion-reduce:animate-none'
        );

        // Add stagger delay for entrance animation
        const delay = index * 100;
        card.style.setProperty('--delay', `${delay}ms`);
        
        // Apply responsive animation classes
        const responsiveClasses = tailwindAnimationOptimizer.getResponsiveAnimationClasses(
          'fadeIn',
          {
            mobile: 'animate-fade-in-fast',
            tablet: 'animate-fade-in',
            desktop: 'animate-slide-in-up'
          }
        );
        
        card.className += ` ${responsiveClasses}`;
      });

      console.log('🎬 Optimized animations setup for', articleCards.length, 'article cards');
      
      // Debug performance if in development
      if (process.env.NODE_ENV === 'development') {
        setTimeout(() => {
          debugAnimationPerformance();
          console.log('📊 Animation Performance Stats:', tailwindAnimationOptimizer.getPerformanceStats());
        }, 2000);
      }
    }
  };

  // Enhanced intersection observer for main articles container
  useEffect(() => {
    if (!articlesRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('articles--animated');
            
            // Apply optimized animations to the main container
            tailwindAnimationOptimizer.animateElement(
              entry.target as HTMLElement,
              'fadeIn',
              {
                delay: 0,
                cache: true,
                promote: true
              }
            );
          }
        });
      },
      { 
        threshold: 0.1,
        rootMargin: '0px 0px -20px 0px'
      }
    );

    observer.observe(articlesRef.current);

    return () => observer.disconnect();
  }, []);

  // Cleanup animations on unmount
  useEffect(() => {
    return () => {
      tailwindAnimationOptimizer.cleanup();
    };
  }, []);

  const categories = ['all', 'development', 'productivity', 'testing', 'tools', 'workflow'];

  const filteredArticles = articles.filter(article => {
    const matchesFilter = filter === 'all' || article.tags.some(tag => {
      switch(filter) {
        case 'development':
          return ['Development', 'Git', 'Version Control', 'Technology', 'Software'].some(t => tag.includes(t));
        case 'productivity':
          return ['Productivity', 'Workflow', 'File Management', 'Tools'].includes(tag);
        case 'testing':
          return ['Testing', 'Software', 'Tools'].some(t => tag.includes(t));
        case 'tools':
          return ['Tools', 'Git', 'Version Control', 'File Management', 'Development'].includes(tag);
        case 'workflow':
          return ['Workflow', 'Productivity', 'File Management'].includes(tag);
        default:
          return true;
      }
    });
    
    const matchesSearch = searchTerm === '' || 
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesFilter && matchesSearch;
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Show loading state with optimized loading animation
  if (loading) {
    return (
      <main className="articles transform-gpu will-change-opacity" ref={articlesRef}>
        <div className="container">
          <div className="articles__header">
            <div className="articles__terminal animate-pulse-bright transform-gpu">
              <div className="articles__terminal-header">
                <span className="articles__terminal-dot articles__terminal-dot--red animate-ping"></span>
                <span className="articles__terminal-dot articles__terminal-dot--yellow animate-ping delay-75"></span>
                <span className="articles__terminal-dot articles__terminal-dot--green animate-ping delay-150"></span>
                <span className="articles__terminal-title">MEDIUM_ARTICLES_FEED_v1.0</span>
              </div>
              <div className="articles__terminal-body">
                <div className="articles__terminal-line">
                  <span className="articles__prompt">griffin@local:~$</span>
                  <span className="articles__command">load --articles --source=local --cache=enabled</span>
                </div>
                <div className="articles__terminal-line animate-fade-in delay-300">
                  <span className="articles__output">Loading articles from local cache...</span>
                </div>
                <div className="articles__terminal-line animate-fade-in delay-500">
                  <span className="articles__output">Articles ready for display...</span>
                </div>
              </div>
            </div>

            <h1 className="articles__title animate-fade-in delay-700">Digital Chronicles</h1>
            <p className="articles__subtitle animate-fade-in delay-900">
              Loading the latest insights from the digital frontier...
            </p>
          </div>
        </div>
      </main>
    );
  }

  // Show error state
  if (error) {
    return (
      <main className="articles animate-fade-in" ref={articlesRef}>
        <div className="container">
          <div className="articles__empty animate-scale-in">
            <div className="articles__empty-icon animate-bounce">⚠️</div>
            <h3>Connection Failed</h3>
            <p>{error}</p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="articles transform-gpu will-change-transform motion-reduce:will-change-auto" ref={articlesRef}>
      <div className="container">
        <div className="articles__header animate-optimized">
          <div className="articles__terminal animate-cyber-glow transform-gpu">
            <div className="articles__terminal-header">
              <span className="articles__terminal-dot articles__terminal-dot--red"></span>
              <span className="articles__terminal-dot articles__terminal-dot--yellow"></span>
              <span className="articles__terminal-dot articles__terminal-dot--green"></span>
              <span className="articles__terminal-title">MEDIUM_ARTICLES_FEED_v1.0</span>
            </div>
            <div className="articles__terminal-body">
              <div className="articles__terminal-line">
                <span className="articles__prompt">griffin@local:~$</span>
                <span className="articles__command">load --articles --source=local --cache=enabled</span>
              </div>
              <div className="articles__terminal-line">
                <span className="articles__output">Found {filteredArticles.length} cached articles...</span>
              </div>
              <div className="articles__terminal-line">
                <span className="articles__output">Source: Local cache (Medium articles)</span>
              </div>
            </div>
          </div>

          <h1 className="articles__title animate-fade-in">Digital Chronicles</h1>
          <p className="articles__subtitle animate-fade-in delay-200">
            Thoughts, insights, and discoveries from the intersection of technology and innovation.
            A curated collection of my published articles and technical writings.
          </p>
        </div>

        <div className="articles__controls animate-slide-in-up delay-400">
          <div className="articles__search transform-gpu">
            <input
              type="text"
              placeholder="Search the archives..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="articles__search-input transition-all duration-300 focus:scale-105 transform-gpu"
            />
            <span className="articles__search-icon">🔍</span>
          </div>

          <div className="articles__filters">
            {categories.map((category, index) => (
              <button
                key={category}
                className={`articles__filter transform-gpu will-change-transform transition-all duration-250 hover:scale-105 motion-reduce:hover:scale-100 ${
                  filter === category ? 'articles__filter--active animate-cyber-glow' : ''
                }`}
                onClick={() => setFilter(category)}
                style={{ '--delay': `${(index + 6) * 100}ms` } as React.CSSProperties}
              >
                {category.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        <div className="articles__grid">
          {filteredArticles.map((article, index) => (
            <div
              key={article.id}
              className="articles__card animate-article-card transform-gpu will-change-transform motion-reduce:animate-none"
              style={{ '--delay': `${index * 100}ms` } as React.CSSProperties}
              data-animation-id={`article-${article.id}`}
            >
              <div className="articles__card-header">
                <ArticleThumbnail 
                  articleId={article.id}
                  emojiThumbnail={article.thumbnail}
                  alt={`Thumbnail for ${article.title}`}
                  className="transform-gpu transition-transform duration-300 hover:scale-110 motion-reduce:hover:scale-100"
                />
                <div className="articles__card-info">
                  <h3 className="articles__card-title">{article.title}</h3>
                  <div className="articles__card-meta">
                    <span className="articles__date">{formatDate(article.publishedDate)}</span>
                    <span className="articles__reading-time">📖 {article.readingTime}</span>
                    <span className="articles__claps">👏 {article.claps?.toLocaleString()}</span>
                    <span className="articles__responses">💬 {article.responses}</span>
                  </div>
                </div>
              </div>

              <p className="articles__card-description">{article.description}</p>

              <div className="articles__tags">
                {article.tags.map((tag) => (
                  <span 
                    key={tag} 
                    className="articles__tag transform-gpu transition-all duration-200 hover:scale-105 motion-reduce:hover:scale-100"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="articles__actions">
                <a
                  href={article.link}
                  className="articles__action articles__action--primary transform-gpu transition-all duration-300 hover:scale-105 motion-reduce:hover:scale-100"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span>Read Article</span>
                  <span className="articles__action-icon">→</span>
                </a>
                <button
                  className="articles__action articles__action--secondary transform-gpu transition-all duration-300 hover:scale-105 motion-reduce:hover:scale-100"
                  onClick={async () => {
                    if (navigator.share) {
                      try {
                        await navigator.share({
                          title: article.title,
                          text: article.description,
                          url: article.link
                        });
                      } catch (error) {
                        // Handle AbortError when user cancels share dialog
                        if (error instanceof Error && error.name === 'AbortError') {
                          // User cancelled share - this is expected behavior, do nothing
                          return;
                        }
                        // Log other unexpected errors
                        console.error('Error sharing article:', error);
                      }
                    }
                  }}
                >
                  <span>Share</span>
                  <span className="articles__action-icon">↗</span>
                </button>
              </div>
            </div>
          ))}
        </div>

        {filteredArticles.length === 0 && (
          <div className="articles__empty animate-scale-in">
            <div className="articles__empty-icon animate-bounce">📝</div>
            <h3>No articles found</h3>
            <p>Try adjusting your search filters or explore different categories.</p>
          </div>
        )}

        <div className="articles__cta animate-fade-in delay-1000">
          <h3>Want to read more?</h3>
          <p>Follow me on Medium for the latest articles and insights.</p>
          <a
            href="https://griffinn.medium.com/"
            className="articles__cta-button transform-gpu transition-all duration-400 hover:scale-105 motion-reduce:hover:scale-100"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span>FOLLOW_ON_MEDIUM</span>
            <span className="articles__cta-icon">➜</span>
          </a>
        </div>
      </div>
    </main>
  );
};

export default Articles; 